// src/components/Sections/AboutSection.tsx
import React from 'react';
import { BookOpen, Code2, Shield, Users } from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';

const AboutSection: React.FC = () => {
  const { state } = useAppContext();
  
  // Translation helper
  const t = (sv: string, en: string) => state.language === 'sv' ? sv : en;

  const strengths = [
    {
      icon: Code2,
      title: t('Full Stack-expertis', 'Full Stack Expertise'),
      description: t(
        'Expert på både frontend (React, Vue, Angular, Blazor) och backend (.NET Core, C#)',
        'Expert in both frontend (React, Vue, Angular, Blazor) and backend (.NET Core, C#)'
      )
    },
    {
      icon: Shield,
      title: t('Arkitektur & Säkerhet', 'Architecture & Security'),
      description: t(
        'Clean Architecture, SOLID-principer, DevSecOps och säkerhetstänk genomsyrar allt jag bygger',
        'Clean Architecture, SOLID principles, DevSecOps and security thinking permeate everything I build'
      )
    },
    {
      icon: Users,
      title: t('Agil utveckling', 'Agile Development'),
      description: t(
        'Van att arbeta i tvärfunktionella team med SCRUM/Kanban, TDD och kontinuerliga leveranser',
        'Experienced working in cross-functional teams with SCRUM/Kanban, TDD and continuous delivery'
      )
    },
    {
      icon: BookOpen,
      title: t('Teknisk författare', 'Technical Author'),
      description: t(
        'Författare till "The Aspiring Full Stack Developer\'s Playbook" med 12 tillhörande applikationer',
        'Author of "The Aspiring Full Stack Developer\'s Playbook" with 12 accompanying applications'
      )
    }
  ];

  const expertise = [
    'C# & .NET Core',
    'Azure Cloud & DevOps',
    'React, Vue & Angular',
    'Docker & Kubernetes',
    'SQL Server & Entity Framework',
    'Microservices & REST APIs',
    'SignalR & Real-time',
    'Domain-Driven Design'
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="about" className="py-20 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left column - Text content */}
          <div>
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-6">
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                {t('Om mig', 'About Me')}
              </span>
            </div>
            
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
              {t(
                'Senior utvecklare med passion för problemlösning',
                'Senior developer with passion for problem-solving'
              )}
            </h2>
            
            <div className="prose prose-lg text-gray-600 dark:text-gray-300 space-y-4">
              <p>
                {t(
                  'Jag har jobbat mycket med Microsoft-stackar genom åren – Azure, DevOps, .NET, C#, REST Web API, SQL Server – och det är en miljö jag känner mig hemma i. Clean Code och SOLID är inte bara något jag läst om, det är sådant jag försöker efterleva varje dag.',
                  'I\'ve worked extensively with Microsoft stacks over the years – Azure, DevOps, .NET, C#, REST Web API, SQL Server – and it\'s an environment where I feel at home. Clean Code and SOLID are not just concepts I\'ve read about, they\'re principles I strive to live by every day.'
                )}
              </p>
              
              <p>
                {t(
                  'Frontend har alltid lockat mig lite extra, kanske för att det är där tekniken möter användaren. Jag har jobbat med Vue, React, Angular, Blazor, Razor Pages – beroende på projekt. Tycker det är viktigt att kunna röra sig mellan olika ramverk utan att fastna i ett tänk.',
                  'Frontend has always attracted me a bit extra, perhaps because that\'s where technology meets the user. I\'ve worked with Vue, React, Angular, Blazor, Razor Pages – depending on the project. I think it\'s important to be able to move between different frameworks without getting stuck in one mindset.'
                )}
              </p>
              
              <p>
                {t(
                  'Det jag gillar mest är att lösa problem på riktigt. Bygga lösningar som är robusta, säkra och som håller över tid. DevOps och DevSecOps har blivit en naturlig del av min vardag – det är svårt att bygga något hållbart idag utan att tänka på hela kedjan från kod till produktion.',
                  'What I enjoy most is solving real problems. Building solutions that are robust, secure and stand the test of time. DevOps and DevSecOps have become a natural part of my daily work – it\'s difficult to build something sustainable today without thinking about the entire chain from code to production.'
                )}
              </p>

              <p className="font-semibold text-gray-900 dark:text-white">
                {t(
                  'Nyfikenheten är min största tillgång. Jag tycker om att lära mig nytt och ger mig gärna in i det som är komplext eller outforskat. Det är där jag oftast skapar mest värde.',
                  'Curiosity is my greatest asset. I enjoy learning new things and readily dive into what is complex or unexplored. That\'s where I often create the most value.'
                )}
              </p>
            </div>

            {/* Expertise tags */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('Teknisk expertis', 'Technical Expertise')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {expertise.map((skill) => (
                  <span
                    key={skill}
                    className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-full text-sm font-medium"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right column - Strengths cards */}
          <div className="space-y-6">
            {strengths.map((strength, index) => {
              const Icon = strength.icon;
              return (
                <div
                  key={index}
                  className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                        <Icon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {strength.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        {strength.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {t(
              'Låt oss bygga något fantastiskt tillsammans',
              'Let\'s build something amazing together'
            )}
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {t(
              'Med 25 års erfarenhet och passion för modern teknik kan jag hjälpa dig att förverkliga dina idéer',
              'With 25 years of experience and passion for modern technology, I can help you realize your ideas'
            )}
          </p>
          <button
            onClick={scrollToContact}
            className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
          >
            {t('Starta en konversation', 'Start a conversation')}
          </button>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;